/** Why this driver could not charge. The steps that did not fire matter too. */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Button,
  Caption1,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { ArrowLeftRegular } from '@fluentui/react-icons'
import {
  EmptyState,
  IDChip,
  KeyValueList,
  KeyValueRow,
  LoadingState,
  MetricPill,
  MonoText,
  SectionCard,
  StatusBadge,
  StepPipeline,
  formatTimestamp,
} from '../platform'
import type { PipelineStep } from '../platform'
import { api } from '../api'
import {
  CANARY_LINE,
  dcsAnsweredInWords,
  dcsAskedInWords,
  decidedByLabel,
  hasNoAuditRow,
  catalogueSaysInWords,
  identifierKindInWords,
  searchTheirLogFor,
  laneLabel,
  readingOf,
} from './decisionReading'
import type { DecisionDetail as Detail } from '../api'
import { useAuth } from '../auth'

/** The OCPP Server's end to end window. */
const OCPP_WINDOW_MS = 3000

const OUTCOME_STATUS = {
  APPROVED: 'success',
  BLOCKED: 'error',
  UNKNOWN: 'warning',
} as const

const STAMP_STATUS = {
  PENDING: 'info',
  BOUND: 'success',
  EXPIRED: 'warning',
} as const

const useStyles = makeStyles({
  // The link sits beside the outcome badge rather than under the card, where
  // a reader looking for "where else does this decision exist" finds it in the
  // same place as on the settlement card.
  searchHint: {
    display: 'block',
    marginTop: '12px',
    color: tokens.colorNeutralForeground3,
  },
  answerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    // One line each. In the card header the flex box is narrow, and without
    // this the link wrapped to four lines beside the outcome badge: measured
    // at 1,440, not guessed.
    whiteSpace: 'nowrap',
  },
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  pipeline: {
    marginBottom: tokens.spacingVerticalM,
    overflowX: 'auto',
  },
  note: {
    display: 'block',
    marginTop: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground3,
  },
  pills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  acknowledge: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    alignItems: 'flex-start',
    marginTop: tokens.spacingVerticalM,
  },
  field: {
    width: '100%',
    maxWidth: '520px',
  },
})

export default function DecisionDetail() {
  const styles = useStyles()
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { principal } = useAuth()
  const [data, setData] = useState<Detail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState('')

  const load = useCallback(
    () => api.get<Detail>(`/decisions/${id}`).then(setData).catch((e) => setError(e.message)),
    [id],
  )

  useEffect(() => {
    void load()
  }, [load])

  const mayAcknowledge = principal?.role === 'operator' || principal?.role === 'admin'

  const acknowledge = () => {
    api
      .post(`/decisions/${id}/escalation`, { note })
      .then(() => {
        setNote('')
        return load()
      })
      .catch((e) => setError(e.message))
  }

  if (error) return <EmptyState title="That decision cannot be read" description={error} />
  if (!data) return <LoadingState label="Reading the decision" />

  const s = data.summary

  // A step that did not match is shown as skipped rather than hidden: an
  // identifier classified OTHER taking the default route looks identical to a
  // routing defect unless the whole evaluation is visible.
  const steps: PipelineStep[] = data.predicate_steps.map((step, i) => ({
    key: `${i}-${step.step}`,
    label: step.step,
    state: step.matched ? 'done' : 'skipped',
  }))

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <Button
          appearance="subtle"
          icon={<ArrowLeftRegular />}
          onClick={() => navigate('/decisions')}
        >
          All decisions
        </Button>
        <IDChip value={s.correlation_id} tone="brand" copyable />
      </div>

      <SectionCard
        title="Answer"
        description="What the OCPP Server was told, and on what basis."
        action={
          <div className={styles.answerActions}>
            {/* Only when the service built one, which needs their row id and a
                configured address. The same rule as the TIM link: a console
                cannot assemble an address out of a base it half knows, and a
                link to the wrong audit row is followed and believed. */}
            {data.ocpi_decision_url ? (
              <Button
                as="a"
                href={data.ocpi_decision_url}
                target="_blank"
                rel="noreferrer noopener"
                appearance="secondary"
              >
                Open the Service OCPI audit row
              </Button>
            ) : null}
            <StatusBadge status={OUTCOME_STATUS[s.outcome]} label={s.outcome} />
          </div>
        }
      >
        <KeyValueList divided>
          {/* WHAT HAPPENED, FIRST AND IN PLAIN WORDS (2026-10-17). */}
          <KeyValueRow label="What arrived" value={readingOf(s).arrived} />
          <KeyValueRow label="Who was asked" value={readingOf(s).asked} />
          <KeyValueRow label="What this service answered" value={readingOf(s).answered} />
          <KeyValueRow label="Time, Berlin" value={formatTimestamp(s.occurred_at)} />
          <KeyValueRow label="Charge point" value={<MonoText>{s.station_id}</MonoText>} />
          <KeyValueRow
            label="Token"
            value={s.identifier ? <MonoText>{s.identifier}</MonoText> : 'not kept'}
          />
          <KeyValueRow
            label="Blind index"
            value={<MonoText tone="muted">{s.identifier_blind_index}</MonoText>}
          />
          {s.canary ? <KeyValueRow label="Canary" value={CANARY_LINE} /> : null}
          <KeyValueRow label="Scheme" value={s.scheme} />
          <KeyValueRow label="Route" value={s.route} />
          {/* THE LANE AND THE DECIDER ARE TWO ROWS NOW. The record keeps
              'dcs' as the lane for every answer on the default route, because
              the Hub feed and the lane verdict are defined over it (DEC-17),
              and this page used to print that word under the heading
              "Authority". So a public card refused by Service OCPI's own
              work-charger rule said DCS, and so did an authorization nothing
              ever answered (FIND-43). */}
          <KeyValueRow
            label="Lane"
            value={s.authority ? laneLabel(s.authority) : 'none, refused before any call'}
          />
          <KeyValueRow label="Decided by" value={decidedByLabel(s.decided_by)} />
          <KeyValueRow label="DCS asked" value={dcsAskedInWords(s.dcs_called)} />
          {s.dcs_called ? (
            <KeyValueRow label="DCS answered" value={dcsAnsweredInWords(s.dcs_response, s.occurred_at)} />
          ) : null}
          <KeyValueRow label="Card issuer" value={s.issuer ?? 'not recorded'} />
          <KeyValueRow label="Kind, as Service OCPI said" value={identifierKindInWords(s.id_token_kind)} />
          {/* Shadow only (DEC-22 step 2). Shown on the row because a
              disagreement between the two estate rules is a fact about THIS
              charge point, and an aggregate on the Overview cannot tell
              somebody looking at one refusal why it happened. */}
          {catalogueSaysInWords(s) ? (
            <KeyValueRow
              label="The E4E list says"
              value={`${catalogueSaysInWords(s)}. It decided nothing here.`}
            />
          ) : null}
          {s.catalogue_shadow && s.catalogue_shadow.current_type !== 'unknown' ? (
            <KeyValueRow
              label="Current the list would report"
              value={`${s.catalogue_shadow.current_type}, from the connector that was used`}
            />
          ) : null}
          <KeyValueRow label="Basis" value={s.basis} />
          {data.authority_call ? (
            <KeyValueRow
              // Null is not a status, it is the absence of a response, and the
              // label has to agree with it: "The authority answered: no
              // status" says two things at once.
              label={
                data.authority_call.status === null
                  ? 'The authority never answered'
                  : 'The authority answered'
              }
              // Nothing where a status would be. The label above has already
              // said no answer arrived, and a symbol that repeats it adds no
              // information: it only asks the reader to decode it. The reason
              // stands on its own.
              value={
                <MonoText>
                  {[data.authority_call.status, data.authority_call.reason]
                    .filter((part) => part !== null && part !== undefined && part !== '')
                    .join(' ')}
                </MonoText>
              }
            />
          ) : (
            <KeyValueRow label="The authority answered" value="no authority was called" />
          )}
        </KeyValueList>
      </SectionCard>

      <SectionCard
        title="How the predicate decided"
        description="In the order the service evaluates them, before any token lookup (ADR-003)."
      >
        <div className={styles.pipeline}>
          <StepPipeline steps={steps} />
        </div>
        <KeyValueList divided>
          {data.predicate_steps.map((step, i) => (
            <KeyValueRow
              key={`${i}-${step.step}`}
              label={step.step}
              value={step.detail ?? (step.matched ? 'matched' : 'did not match')}
            />
          ))}
        </KeyValueList>
        <Caption1 className={styles.note}>
          Steps that did not match are shown as well. An identifier classified OTHER taking the
          default route looks identical to a routing defect unless the evaluation is visible.
        </Caption1>
      </SectionCard>

      <SectionCard
        title="Session"
        description="The binding of charge point and identifier to the authority that answered."
        action={
          data.stamp ? (
            <StatusBadge status={STAMP_STATUS[data.stamp.state]} label={data.stamp.state} />
          ) : undefined
        }
      >
        {data.stamp ? (
          <KeyValueList divided>
            <KeyValueRow
              label="Bound at, Berlin"
              value={data.stamp.bound_at ? formatTimestamp(data.stamp.bound_at) : 'not yet bound'}
            />
            <KeyValueRow
              label="Broker transaction"
              value={
                data.stamp.broker_transaction_id ? (
                  <MonoText>{data.stamp.broker_transaction_id}</MonoText>
                ) : (
                  'none carried'
                )
              }
            />
            <KeyValueRow
              label="Session"
              value={
                data.stamp.session_id ? (
                  <MonoText>{data.stamp.session_id}</MonoText>
                ) : (
                  'no session started'
                )
              }
            />
          </KeyValueList>
        ) : (
          <Caption1 className={styles.note}>
            No stamp. Either the authorization was refused, or this session was authorized before
            the service took the registration.
          </Caption1>
        )}
      </SectionCard>

      {data.settlement ? (
        <SectionCard
          title="Settlement"
          description="What happened to the money after the session ended."
          action={
            // Only when the service gave us one. A support question crosses two
            // consoles: the station and the card are here, because TIM has no
            // per charge point granularity by design, and what was submitted is
            // there. The correlation id is the same value on both sides, so the
            // hop is one link and used to be a copied identifier and two tabs.
            data.tim_submission_url ? (
              <Button
                as="a"
                href={data.tim_submission_url}
                target="_blank"
                rel="noreferrer noopener"
                appearance="secondary"
              >
                Open this session in the TIM console
              </Button>
            ) : undefined
          }
        >
          <KeyValueList divided>
            <KeyValueRow
              label="The authority answered"
              value={<MonoText>{data.settlement.status ?? 'no answer'}</MonoText>}
            />
            <KeyValueRow label="Outcome" value={data.settlement.outcome} />
            <KeyValueRow
              label="Field that differs"
              value={
                data.settlement.differing_field ? (
                  <MonoText>{data.settlement.differing_field}</MonoText>
                ) : (
                  'none named'
                )
              }
            />
            <KeyValueRow
              label="Acknowledged by"
              value={data.settlement.acknowledged_by ?? 'nobody yet'}
            />
            <KeyValueRow label="Note" value={data.settlement.note ?? 'none recorded'} />
          </KeyValueList>

          {data.settlement.escalated && !data.settlement.acknowledged_by ? (
            <div className={styles.acknowledge}>
              <MessageBar intent="error">
                <MessageBarBody>
                  <MessageBarTitle>This one needs a decision, not a retry</MessageBarTitle>
                  The authority answered 422: the same session id arrived carrying different
                  identifying facts. Either two real sessions collided on one key, or a payload was
                  corrected after it was sent. Resending resolves neither.
                </MessageBarBody>
              </MessageBar>
              <Input
                className={styles.field}
                value={note}
                onChange={(_, d) => setNote(d.value)}
                placeholder="What did you decide, and why. Stored verbatim."
                aria-label="What did you decide, and why"
                disabled={!mayAcknowledge}
              />
              <Button
                appearance="primary"
                onClick={acknowledge}
                disabled={!mayAcknowledge || !note}
              >
                {mayAcknowledge ? 'Acknowledge' : 'Operators and admins only'}
              </Button>
            </div>
          ) : null}
        </SectionCard>
      ) : null}

      <SectionCard
        title="Where the time went"
        description={`The OCPP Server allows ${OCPP_WINDOW_MS} ms end to end.`}
      >
        <div className={styles.pills}>
          <MetricPill label="This service" value={`${data.latency_breakdown.own_ms} ms`} />
          <MetricPill label="Downstream" value={`${data.latency_breakdown.downstream_ms} ms`} />
          <MetricPill
            label="Total"
            value={`${s.latency_ms} ms`}
            tone={s.latency_ms > OCPP_WINDOW_MS * 0.8 ? 'warning' : 'success'}
          />
        </div>
        {/* WHERE TO LOOK WHEN THERE IS NO LINK. A timed out call returns no
            body, so no audit row id came back and the button above cannot
            exist: the decisions somebody most wants to look up are exactly the
            ones with no key. Their log is searchable by charge point and time,
            and this service recorded both, so the window is on the page rather
            than in a runbook nobody opens while reading a row. */}
        {hasNoAuditRow(s) && !data.ocpi_decision_url ? (
          <Caption1 className={styles.searchHint}>
            Service OCPI never answered, so there is no audit row to link to. In their
            dashboard, search the auth log for charge point {s.station_id} between{' '}
            {formatTimestamp(searchTheirLogFor(s, data.latency_breakdown.downstream_ms)?.from)}{' '}
            and{' '}
            {formatTimestamp(searchTheirLogFor(s, data.latency_breakdown.downstream_ms)?.to)}.
          </Caption1>
        ) : null}
      </SectionCard>
    </div>
  )
}
